import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateHousehold } from "@/api/households";
import { ErrorNote, Field, Panel, buttonClass, inputClass } from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The create-your-first-household screen.
 *
 * A base currency is asked for once, here, and never again: `model.md` §5.6
 * denominates every `base_amount_minor` in the household in it, and changing it
 * later would reinterpret every stored figure. Saying so on the form is cheaper
 * than a support conversation later.
 */
export function OnboardingPage() {
  const t = useT();
  const navigate = useNavigate();
  const create = useCreateHousehold();
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("EUR");

  const violation = violationsOf(t, create.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      { name, base_currency_code: currency },
      // Accounts, not the dashboard. A household created a second ago has
      // nothing for the dashboard to be about, and the next thing it needs is an
      // account — the overview is where returning visits land.
      { onSuccess: (household) => navigate(`/households/${household.id}/accounts`) },
    );
  }

  return (
    <div className="mx-auto max-w-md py-6 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{t("onboarding.title")}</h1>
      <p className="mt-2 text-sm text-muted">{t("onboarding.lead")}</p>

      <Panel className="mt-6" as="section">
        <form onSubmit={onSubmit} noValidate>
          <Field label={t("onboarding.name")} htmlFor="household-name" error={violation("name")}>
            <input
              id="household-name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("onboarding.namePlaceholder")}
              autoFocus
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            />
          </Field>

          <Field
            label={t("onboarding.baseCurrency")}
            htmlFor="household-currency"
            hint={t("onboarding.baseCurrencyHint")}
            error={violation("base_currency_code")}
          >
            <select
              id="household-currency"
              name="base_currency_code"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            >
              <option value="EUR">{t("currency.EUR")}</option>
              <option value="CHF">{t("currency.CHF")}</option>
              <option value="GBP">{t("currency.GBP")}</option>
              <option value="USD">{t("currency.USD")}</option>
            </select>
          </Field>

          <button
            type="submit"
            disabled={create.isPending}
            className={`${buttonClass} mt-6 min-h-11 w-full`}
          >
            {create.isPending ? t("onboarding.creating") : t("onboarding.title")}
          </button>
        </form>

        <ErrorNote error={create.error} />
      </Panel>
    </div>
  );
}
